import { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { submitEmployer, submitCandidate, submitContact } from "../lib/api";

const inputCls =
  "w-full bg-white border border-slate-200 rounded-md px-4 py-3 text-navy placeholder:text-slate-400 focus:outline-none focus:border-sapphire focus:ring-2 focus:ring-sapphire/15 transition-colors";

const Field = ({ label, name, type = "text", required, value, onChange, placeholder, testid, textarea, rows = 4 }) => (
  <label className="block">
    <span className="block text-sm font-medium text-navy mb-1.5">
      {label}{required && <span className="text-sapphire"> *</span>}
    </span>
    {textarea ? (
      <textarea name={name} required={required} value={value} onChange={onChange} rows={rows}
        placeholder={placeholder} data-testid={testid} className={inputCls} />
    ) : (
      <input name={name} type={type} required={required} value={value} onChange={onChange}
        placeholder={placeholder} data-testid={testid} className={inputCls} />
    )}
  </label>
);

const useForm = (initial, submitFn) => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitFn(form);
      toast.success("Submitted successfully");
      setDone(true);
      setForm(initial);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  return { form, onChange, submit, loading, done, setDone };
};

const SubmitBtn = ({ loading, children, testid }) => (
  <button type="submit" disabled={loading} data-testid={testid}
    className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-sapphire hover:bg-sapphire-dark text-white font-semibold disabled:opacity-60 transition-colors">
    {loading && <Loader2 size={18} className="animate-spin" />}
    {children}
  </button>
);

const Success = ({ testid, onReset, text }) => (
  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid={testid}>
    <CheckCircle2 className="mx-auto text-emerald-500 mb-3" size={40} />
    <p className="text-navy font-semibold text-lg">{text}</p>
    <button onClick={onReset} className="mt-4 text-sm font-semibold text-sapphire hover:underline">Submit another</button>
  </div>
);

export const EmployerForm = () => {
  const f = useForm(
    { name: "", company: "", email: "", phone: "", requirement: "", positions: "", message: "" },
    submitEmployer
  );
  if (f.done) return <Success testid="employer-success" onReset={() => f.setDone(false)} text="Your hiring requirement has been received. Our team will reach out shortly." />;
  return (
    <form onSubmit={f.submit} className="space-y-4" data-testid="employer-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Name" name="name" required value={f.form.name} onChange={f.onChange} placeholder="Your name" testid="employer-name" />
        <Field label="Company" name="company" required value={f.form.company} onChange={f.onChange} placeholder="Company name" testid="employer-company" />
        <Field label="Email" name="email" type="email" required value={f.form.email} onChange={f.onChange} placeholder="you@company.com" testid="employer-email" />
        <Field label="Phone" name="phone" required value={f.form.phone} onChange={f.onChange} placeholder="+91 XXXXX XXXXX" testid="employer-phone" />
        <Field label="Hiring Requirement" name="requirement" required value={f.form.requirement} onChange={f.onChange} placeholder="e.g. Senior Java Developers" testid="employer-requirement" />
        <Field label="Number of Positions" name="positions" value={f.form.positions} onChange={f.onChange} placeholder="e.g. 5" testid="employer-positions" />
      </div>
      <Field label="Message" name="message" textarea value={f.form.message} onChange={f.onChange} placeholder="Tell us more about the roles, skills and timelines" testid="employer-message" />
      <SubmitBtn loading={f.loading} testid="employer-submit">Submit a Hiring Requirement</SubmitBtn>
    </form>
  );
};

export const CandidateForm = () => {
  const f = useForm(
    { name: "", email: "", phone: "", resume_link: "", current_location: "", preferred_location: "", skills: "", experience: "", preferred_role: "" },
    submitCandidate
  );
  if (f.done) return <Success testid="candidate-success" onReset={() => f.setDone(false)} text="Thanks! Your profile has been received. We'll connect you with the right opportunities." />;
  return (
    <form onSubmit={f.submit} className="space-y-4" data-testid="candidate-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Name" name="name" required value={f.form.name} onChange={f.onChange} placeholder="Your name" testid="candidate-name" />
        <Field label="Email" name="email" type="email" required value={f.form.email} onChange={f.onChange} placeholder="you@email.com" testid="candidate-email" />
        <Field label="Phone" name="phone" required value={f.form.phone} onChange={f.onChange} placeholder="+91 XXXXX XXXXX" testid="candidate-phone" />
        <Field label="Resume Link (Drive / URL)" name="resume_link" value={f.form.resume_link} onChange={f.onChange} placeholder="https://..." testid="candidate-resume" />
        <Field label="Current Location" name="current_location" value={f.form.current_location} onChange={f.onChange} placeholder="City" testid="candidate-current-location" />
        <Field label="Preferred Location" name="preferred_location" value={f.form.preferred_location} onChange={f.onChange} placeholder="City / Remote" testid="candidate-preferred-location" />
        <Field label="Experience" name="experience" value={f.form.experience} onChange={f.onChange} placeholder="e.g. 5 years" testid="candidate-experience" />
        <Field label="Preferred Role" name="preferred_role" value={f.form.preferred_role} onChange={f.onChange} placeholder="e.g. Backend Engineer" testid="candidate-role" />
      </div>
      <Field label="Skills" name="skills" textarea rows={3} value={f.form.skills} onChange={f.onChange} placeholder="e.g. React, Node.js, AWS, SQL" testid="candidate-skills" />
      <SubmitBtn loading={f.loading} testid="candidate-submit">Submit Your Profile</SubmitBtn>
    </form>
  );
};

export const ContactForm = () => {
  const f = useForm(
    { name: "", company: "", email: "", phone: "", requirement: "", message: "" },
    submitContact
  );
  if (f.done) return <Success testid="contact-success" onReset={() => f.setDone(false)} text="Message sent. We'll get back to you soon." />;
  return (
    <form onSubmit={f.submit} className="space-y-4" data-testid="contact-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Name" name="name" required value={f.form.name} onChange={f.onChange} placeholder="Your name" testid="contact-name" />
        <Field label="Company" name="company" value={f.form.company} onChange={f.onChange} placeholder="Company name" testid="contact-company" />
        <Field label="Email" name="email" type="email" required value={f.form.email} onChange={f.onChange} placeholder="you@company.com" testid="contact-email" />
        <Field label="Phone" name="phone" value={f.form.phone} onChange={f.onChange} placeholder="+91 XXXXX XXXXX" testid="contact-phone" />
      </div>
      <Field label="Requirement" name="requirement" value={f.form.requirement} onChange={f.onChange} placeholder="What do you need help with?" testid="contact-requirement" />
      <Field label="Message" name="message" required textarea value={f.form.message} onChange={f.onChange} placeholder="Your message" testid="contact-message" />
      <SubmitBtn loading={f.loading} testid="contact-submit">Send Message</SubmitBtn>
    </form>
  );
};
